import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation/Navigation';
import authService from '../../services/authService';
import './CategoryDetailsPage.css';

const CategoryDetailsPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const [category, setCategory] = useState<any>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [tagForm, setTagForm] = useState({
    name: '',
    image_url: '',
    text_color: '',
    language: 'hindi',
  });
  const [submittingTag, setSubmittingTag] = useState(false);

  useEffect(() => {
    fetchCategoryDetails();
    fetchCategoryTags();
  }, [categoryId]);

  const fetchCategoryDetails = async () => {
    try {
      setLoading(true);
      const categories = await authService.getCategories();
      const foundCategory = categories.find(
        (cat: any) => cat.id === categoryId
      );
      if (foundCategory) {
        setCategory(foundCategory);
      } else {
        setError('Category not found');
      }
    } catch (error) {
      console.error('Error fetching category details:', error);
      setError('Failed to load category details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryTags = async () => {
    try {
      setTagsLoading(true);
      if (categoryId) {
        const categoryTags = await authService.getCategoryTags(categoryId);
        setTags(categoryTags);
      }
    } catch (error) {
      console.error('Error fetching category tags:', error);
      setError('Failed to load tags');
    } finally {
      setTagsLoading(false);
    }
  };

  const handleAddTag = () => {
    setShowAddTagModal(true);
    setError(null);
  };

  const handleCloseAddTagModal = () => {
    setShowAddTagModal(false);
    setError(null);
    setSubmittingTag(false);
    setTagForm({
      name: '',
      image_url: '',
      text_color: '',
      language: 'hindi',
    });
  };

  const handleTagFormChange = (field: string, value: string) => {
    setTagForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmitTag = async () => {
    try {
      setSubmittingTag(true);
      setError(null);

      if (!tagForm.name.trim()) {
        setError('Please enter a tag name');
        return;
      }

      const payload = {
        name: tagForm.name.trim(),
        category_id: categoryId!,
        image_url: tagForm.image_url.trim(),
        text_color: tagForm.text_color.trim(),
        language: tagForm.language,
      };

      await authService.createCategoryTag(payload);
      await fetchCategoryTags();
      handleCloseAddTagModal();
      setError(null);
    } catch (error) {
      console.error('Error creating tag:', error);
      setError('Failed to create tag. Please try again.');
    } finally {
      setSubmittingTag(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-container">
          <div className="loading-spinner">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !category) {
    return (
      <div className="dashboard-page">
        <div className="main-content">
          <Navigation showBack={false} />
          <div className="error-container">
            <div className="error-message">
              <svg
                width="60"
                height="60"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              <h3>Error</h3>
              <p>{error}</p>
              <button
                className="retry-button"
                onClick={() => navigate('/category')}
              >
                Back to Categories
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      {/* Top orange strip */}
      <div className="top-strip"></div>

      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h2 className="dashboard-title">Admin Dashboard</h2>
        </div>

        <div className="sidebar-divider"></div>

        <nav className="sidebar-nav">
          <button className="nav-button" onClick={() => navigate('/category')}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Categories
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <Navigation showBack={false} />
        <div className="content-header">
          <div className="header-content">
            <h1 className="page-title">{category?.name || 'Category'} Tags</h1>
            <p className="page-subtitle">Manage tags for this category</p>
          </div>
          <button className="add-challenge-button" onClick={handleAddTag}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Tag
          </button>
        </div>

        <div className="tags-container">
          {error && (
            <div className="error-message">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
              {error}
            </div>
          )}

          <div className="tags-header">
            {!tagsLoading && (
              <p className="tags-count">
                {tags.length} tag{tags.length !== 1 ? 's' : ''} found
              </p>
            )}
          </div>

          {tagsLoading ? (
            <div className="tags-loading-container">
              <div className="loading-spinner"></div>
              <span>Loading tags...</span>
            </div>
          ) : tags.length > 0 ? (
            <div className="tags-grid">
              {tags.map(tag => (
                <div key={tag.id} className="tag-card">
                  <div className="tag-content">
                    <h3 className="tag-name">{tag.name}</h3>
                    <p className="tag-slug">#{tag.id}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="tags-empty-container">
              <div className="tags-empty-message">
                <svg
                  width="60"
                  height="60"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                  <line x1="7" y1="7" x2="7.01" y2="7" />
                </svg>
                <h3>No tags available</h3>
                <p>
                  No tags found for this category. Add your first tag to get
                  started.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAddTagModal && (
        <div className="modal-overlay">
          <div className="modal-content add-tag-modal">
            <div className="modal-header">
              <h2>Add New Tag</h2>
              <button className="modal-close" onClick={handleCloseAddTagModal}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              {error && (
                <div className="modal-error">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  {error}
                </div>
              )}

              <div className="form-group">
                <label htmlFor="name">Tag Name</label>
                <input
                  type="text"
                  id="name"
                  value={tagForm.name}
                  onChange={e => handleTagFormChange('name', e.target.value)}
                  placeholder="Enter tag name (e.g., Technology, Health)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image_url">Image URL</label>
                <input
                  type="url"
                  id="image_url"
                  value={tagForm.image_url}
                  onChange={e =>
                    handleTagFormChange('image_url', e.target.value)
                  }
                  placeholder="Enter image URL"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="text_color">Text Color</label>
                <input
                  type="text"
                  id="text_color"
                  value={tagForm.text_color}
                  onChange={e =>
                    handleTagFormChange('text_color', e.target.value)
                  }
                  placeholder="Enter color code (e.g., FF713F12)"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="tag_language">Language</label>
                <select
                  id="tag_language"
                  value={tagForm.language}
                  onChange={e =>
                    handleTagFormChange('language', e.target.value)
                  }
                  className="form-input"
                >
                  <option value="hindi">Hindi</option>
                  <option value="hinglish">Hinglish</option>
                </select>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCloseAddTagModal}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleSubmitTag}
                disabled={submittingTag}
              >
                {submittingTag ? (
                  <>
                    <div className="button-spinner"></div>
                    Creating Tag...
                  </>
                ) : (
                  'Create Tag'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryDetailsPage;
